// List on the left, the open item in a panel on the right. Each side scrolls on
// its own, so opening an item never pushes the list down and a long list never
// pushes the panel away. Lists cap how many rows they render; see ROW_CAP.
import React from 'react';
import { GEyebrow, GH2, GPill } from './atoms';

/** Lists render at most this many rows; past it they ask for a narrower search. */
export const ROW_CAP = 300;

const WIDTH_KEY = 'pd-detail-width';
const DEFAULT_WIDTH = 500;
const MIN_WIDTH = 320;

function storedWidth(): number {
  try { const v = Number(localStorage.getItem(WIDTH_KEY)); if (Number.isFinite(v) && v >= MIN_WIDTH) return v; } catch { /* storage unavailable */ }
  return DEFAULT_WIDTH;
}
function remember(w: number): void { try { localStorage.setItem(WIDTH_KEY, String(w)); } catch { /* ignore */ } }
/** never narrower than MIN_WIDTH, never wider than 70% of the window */
function clamp(w: number): number { return Math.max(MIN_WIDTH, Math.min(Math.round(window.innerWidth * 0.7), Math.round(w))); }

export function MasterDetail({ list, detail }: { list: React.ReactNode; detail: React.ReactNode | null }) {
  const [width, setWidth] = React.useState<number>(storedWidth);
  const latest = React.useRef(width);
  latest.current = width;

  // drag the panel's left edge: moving left widens, moving right narrows
  const onMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX; const startW = latest.current;
    const onMove = (ev: MouseEvent) => { const w = clamp(startW - (ev.clientX - startX)); latest.current = w; setWidth(w); };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      remember(latest.current);
    };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  }, []);
  const reset = React.useCallback(() => { setWidth(DEFAULT_WIDTH); remember(DEFAULT_WIDTH); }, []);

  return (
    <div className="pd-split" data-testid="master-detail">
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: '22px 28px 40px' }}>{list}</div>
      {detail && (
        <aside data-testid="detail-panel" style={{ position: 'relative', width, maxWidth: '70%', flexShrink: 0, overflow: 'auto', borderLeft: '1px solid var(--bd-soft)', background: 'var(--bg-panel)', padding: '18px 24px 40px' }}>
          <div role="separator" aria-orientation="vertical" aria-label="Resize the panel" title="Drag to resize · double-click to reset" data-testid="detail-resize"
            className="tree-resize" onMouseDown={onMouseDown} onDoubleClick={reset}
            style={{ position: 'absolute', left: 0, top: 0, width: 12, height: '100%', cursor: 'col-resize', zIndex: 5, display: 'flex', alignItems: 'center' }}>
            <div className="tree-resize-grip" style={{ width: 3, height: 40, marginLeft: 2, borderRadius: 'var(--r-ctl)', background: 'var(--bd-strong)', opacity: 0.45, transition: 'opacity 0.12s, background 0.12s' }} />
          </div>
          {detail}
        </aside>
      )}
    </div>
  );
}

/** Panel header: title, a machine-value eyebrow, Close. */
export function DetailHeader({ title, eyebrow, onClose, right }: { title: React.ReactNode; eyebrow?: React.ReactNode; onClose: () => void; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <GH2 size={17} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{title}</GH2>
        {eyebrow && <GEyebrow style={{ fontSize: 11.5, marginTop: 4, overflowWrap: 'anywhere' }}>{eyebrow}</GEyebrow>}
      </div>
      {right}
      <GPill onClick={onClose} title="Close the panel (Esc)">Close</GPill>
    </div>
  );
}

/** A section label inside the panel. */
export function DetailSection({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 18 }}>
      <GEyebrow style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</GEyebrow>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

/** Rows beyond the cap are not rendered; say so. */
export function RowCapNote({ shown, total }: { shown: number; total: number }) {
  if (total <= shown) return null;
  return <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-4)', marginTop: 10 }}>Showing {shown} of {total}. Narrow the search to see the rest.</div>;
}

/** Esc closes the open panel. */
export function useEscape(onClose: () => void, active: boolean): void {
  React.useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, active]);
}

/** Row style for the selected item. */
export const selectedRow = (on: boolean): React.CSSProperties => ({ cursor: 'pointer', background: on ? 'var(--bg-elev)' : undefined });
